import { Asset, AssetHolding } from '../types';
import { supabase } from '../lib/supabaseClient';
import { batchUpdateHoldingPrices } from './storage';

// ── Types ──────────────────────────────────────────────────────────────────────
export interface PriceResult {
  code:      string;
  price:     number;   // VND
  source:    string;
  fetchedAt: string;
}

export interface UpdateProgress {
  pct:     number;    // 0–100
  phase:   1 | 2 | 3 | 4;
  message: string;
  done:    boolean;
  results: PriceResult[];
  errors:  string[];
}

// ── Source mappings ────────────────────────────────────────────────────────────

/** asset.code (uppercase) → CoinGecko ID */
const COINGECKO_IDS: Record<string, string> = {
  BTC: 'bitcoin', ETH: 'ethereum', BNB: 'binancecoin',
  SOL: 'solana',  ADA: 'cardano',  USDT: 'tether',
};

/** Codes that map to international gold, to be converted to VNĐ/lượng */
const GOLD_CODES = new Set(['SJC', 'PNJ', 'VANG', 'GOLD', 'XAU']);

/** Detect if a code looks like a Vietnamese stock ticker (2–4 uppercase letters) */
function isVNStock(code: string): boolean {
  return /^[A-Z]{2,4}$/.test(code) && !COINGECKO_IDS[code] && !GOLD_CODES.has(code);
}

// ── Fetchers ───────────────────────────────────────────────────────────────────

async function fetchCrypto(codes: string[]): Promise<PriceResult[]> {
  const eligible = codes.filter(c => COINGECKO_IDS[c]);
  if (!eligible.length) return [];

  const ids = eligible.map(c => COINGECKO_IDS[c]).join(',');
  const url = `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=vnd`;
  const res = await fetch(url, { signal: AbortSignal.timeout(10_000) });
  if (!res.ok) throw new Error(`CoinGecko HTTP ${res.status}`);
  const data = await res.json();
  const now = new Date().toISOString();

  return eligible.reduce<PriceResult[]>((acc, code) => {
    const id    = COINGECKO_IDS[code];
    const price = data[id]?.vnd;
    if (price) acc.push({ code, price: Number(price), source: 'coingecko', fetchedAt: now });
    return acc;
  }, []);
}

async function fetchOneStock(ticker: string): Promise<PriceResult | null> {
  // TCBS public price-history API — no auth required
  const to   = Math.floor(Date.now() / 1000);
  const from = to - 86_400 * 5; // 5 days back to catch latest close
  const url  = `https://apipubaws.tcbs.com.vn/stock-insight/v2/stock/price-histories` +
               `?ticker=${ticker}&type=stock&resolution=D&from=${from}&to=${to}`;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(8_000) });
    if (!res.ok) return null;
    const json = await res.json();
    const items: any[] = json?.data ?? json?.stockRealtimeList ?? [];
    if (!items.length) return null;

    const last = items[items.length - 1];
    // TCBS returns price in thousands VND (e.g., 58.0 → 58,000 VND)
    const raw = Number(last.close ?? last.closePrice ?? last.price ?? 0);
    if (!raw) return null;
    const price = raw < 1_000 ? raw * 1_000 : raw; // normalise to VND

    return { code: ticker, price, source: 'tcbs', fetchedAt: new Date().toISOString() };
  } catch {
    return null;
  }
}

async function fetchStocks(codes: string[]): Promise<PriceResult[]> {
  const results = await Promise.allSettled(codes.map(fetchOneStock));
  return results.reduce<PriceResult[]>((acc, r) => {
    if (r.status === 'fulfilled' && r.value) acc.push(r.value);
    return acc;
  }, []);
}

async function fetchGold(codes: string[]): Promise<PriceResult[]> {
  if (!codes.length) return [];

  // Fetch international gold (USD/troy-oz) + USD→VND exchange rate in parallel
  const [goldSettled, fxSettled] = await Promise.allSettled([
    fetch('https://api.metals.live/v1/spot/gold',      { signal: AbortSignal.timeout(8_000) }),
    fetch('https://open.er-api.com/v6/latest/USD',     { signal: AbortSignal.timeout(8_000) }),
  ]);

  let usdPerOz = 0;
  let usdVnd   = 25_400; // fallback exchange rate

  if (goldSettled.status === 'fulfilled' && goldSettled.value.ok) {
    const gd = await goldSettled.value.json();
    // metals.live returns { price: 2350 } or [{ price: ... }]
    usdPerOz = Number(Array.isArray(gd) ? gd[0]?.price : gd?.price) || 0;
  }
  if (fxSettled.status === 'fulfilled' && fxSettled.value.ok) {
    const fx = await fxSettled.value.json();
    usdVnd = Number(fx?.rates?.VND) || usdVnd;
  }
  if (!usdPerOz) return [];

  // 1 lượng (tael VN) = 37.5 g;  1 troy oz = 31.1035 g
  // VNĐ per lượng = usdPerOz × (37.5 / 31.1035) × usdVnd
  const pricePerLuong = Math.round(usdPerOz * (37.5 / 31.1035) * usdVnd);
  const now = new Date().toISOString();

  return codes.map(code => ({
    code,
    // SJC carries a local premium (~15–20% above international)
    price:     code === 'SJC' ? Math.round(pricePerLuong * 1.18) : pricePerLuong,
    source:    'metals.live',
    fetchedAt: now,
  }));
}

// ── Save to market_prices table (best-effort — table may not exist yet) ────────
async function persistToMarketPrices(results: PriceResult[]) {
  if (!results.length) return;
  try {
    const rows = results.map(r => ({
      code:       r.code,
      price:      r.price,
      source:     r.source,
      fetched_at: r.fetchedAt,
    }));
    await supabase
      .from('market_prices')
      .upsert(rows, { onConflict: 'code' });
  } catch {
    // Table doesn't exist yet — silently skip
  }
}

// ── Main entry ─────────────────────────────────────────────────────────────────
export async function fetchAllPrices(
  assets: Asset[],
  onProgress: (p: UpdateProgress) => void,
  holdings?: AssetHolding[],   // vị thế trong composite assets (tùy chọn)
): Promise<PriceResult[]> {
  // Classify asset codes
  const cryptoCodes: string[] = [];
  const stockCodes:  string[] = [];
  const goldCodes:   string[] = [];

  for (const a of assets) {
    const code = (a.code || '').toUpperCase().trim();
    if (!code) continue;
    if (COINGECKO_IDS[code])  cryptoCodes.push(code);
    else if (GOLD_CODES.has(code)) goldCodes.push(code);
    else if (isVNStock(code)) stockCodes.push(code);
  }

  // Deduplicate
  const uniq = (arr: string[]) => Array.from(new Set(arr));
  const crypto = uniq(cryptoCodes);
  const stocks = uniq(stockCodes);
  const gold   = uniq(goldCodes);

  const allResults: PriceResult[] = [];
  const errors:     string[]      = [];

  const emit = (pct: number, phase: 1|2|3|4, message: string, done = false) =>
    onProgress({ pct, phase, message, done, results: [...allResults], errors: [...errors] });

  // ── Phase 1 — Vàng (0 → 30%) ──────────────────────────────────────────────
  emit(5, 1, 'Đang kết nối thị trường Vàng...');
  try {
    const g = await fetchGold(gold);
    allResults.push(...g);
  } catch (e: any) {
    errors.push(`Vàng: ${e?.message ?? 'lỗi không xác định'}`);
  }
  emit(30, 1, gold.length ? `Đã lấy giá ${gold.join(', ')}` : 'Không có tài sản Vàng');

  // ── Phase 2 — Chứng khoán & Quỹ (30 → 70%) ───────────────────────────────
  emit(35, 2, 'Đang lấy giá Chứng khoán & Quỹ...');
  try {
    const s = await fetchStocks(stocks);
    allResults.push(...s);
    const missed = stocks.filter(c => !s.some(r => r.code === c));
    if (missed.length) errors.push(`Không lấy được: ${missed.join(', ')}`);
  } catch (e: any) {
    errors.push(`Chứng khoán: ${e?.message ?? 'lỗi không xác định'}`);
  }
  emit(70, 2, stocks.length ? `Đã cập nhật ${stocks.length} mã` : 'Không có cổ phiếu');

  // ── Phase 3 — Crypto & Tỷ giá (70 → 95%) ─────────────────────────────────
  emit(75, 3, 'Đang cập nhật tỷ giá Crypto & Lãi suất...');
  try {
    const c = await fetchCrypto(crypto);
    allResults.push(...c);
  } catch (e: any) {
    errors.push(`Crypto: ${e?.message ?? 'lỗi không xác định'}`);
  }
  emit(95, 3, crypto.length ? `Đã cập nhật ${crypto.length} token` : 'Không có Crypto');

  // ── Phase 4 — Cập nhật giá holdings (95 → 98%) ────────────────────────────
  if (holdings && holdings.length > 0) {
    emit(95, 4, 'Đang cập nhật giá micro-assets...');
    try {
      // Phân loại symbol của holdings theo provider
      const hCryptoCodes = Array.from(new Set(
        holdings.filter(h => COINGECKO_IDS[h.symbol.toUpperCase()]).map(h => h.symbol.toUpperCase())
      ));
      const hStockCodes = Array.from(new Set(
        holdings.filter(h => isVNStock(h.symbol.toUpperCase())).map(h => h.symbol.toUpperCase())
      ));
      const hGoldCodes = Array.from(new Set(
        holdings.filter(h => GOLD_CODES.has(h.symbol.toUpperCase())).map(h => h.symbol.toUpperCase())
      ));

      // Loại bỏ những symbol đã được fetch ở trên (tránh gọi API lần 2)
      const fetchedCodes = new Set(allResults.map(r => r.code.toUpperCase()));
      const newCrypto = hCryptoCodes.filter(c => !fetchedCodes.has(c));
      const newStocks = hStockCodes.filter(c => !fetchedCodes.has(c));
      const newGold   = hGoldCodes.filter(c => !fetchedCodes.has(c));

      const [hCr, hSt, hGo] = await Promise.allSettled([
        newCrypto.length ? fetchCrypto(newCrypto) : Promise.resolve([]),
        newStocks.length ? fetchStocks(newStocks) : Promise.resolve([]),
        newGold.length   ? fetchGold(newGold)     : Promise.resolve([]),
      ]);

      const holdingResults: PriceResult[] = [
        ...(hCr.status === 'fulfilled' ? hCr.value : []),
        ...(hSt.status === 'fulfilled' ? hSt.value : []),
        ...(hGo.status === 'fulfilled' ? hGo.value : []),
      ];
      allResults.push(...holdingResults);

      // Gộp tất cả kết quả liên quan đến holdings và lưu vào DB
      const allHoldingSymbols = new Set(holdings.map(h => h.symbol.toUpperCase()));
      const relevantResults = allResults.filter(r => allHoldingSymbols.has(r.code.toUpperCase()));
      if (relevantResults.length > 0) {
        await batchUpdateHoldingPrices(
          relevantResults.map(r => ({ symbol: r.code, marketPrice: r.price, provider: r.source }))
        );
      }
    } catch (e: any) {
      errors.push(`Holdings: ${e?.message ?? 'lỗi không xác định'}`);
    }
  }

  // ── Phase 4 — Lưu & hoàn tất (98 → 100%) ─────────────────────────────────
  await persistToMarketPrices(allResults);
  emit(100, 4, 'Hoàn tất! NAV gia sản đã được cập nhật.', true);

  return allResults;
}
